import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Fab,
  Chip,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Memory as MemoryIcon,
  Sensors as SensorsIcon
} from '@mui/icons-material';
import { fetchTarjetaById, updateTarjeta } from '../../../actions/tarjeta';
import { cambiarEstadoTarjeta } from '../../../actions/dispositivos';
import { useModuloPermissions } from '../../../hooks/useModuloPermissions';
import DeviceControlButton from '../../../components/device-control-button';
import Bread from '../../../components/breadcrumb';
import { routeTarjetaEdit } from '../../../utils/routesBread';
import Swal from 'sweetalert2';

const EditTarjeta = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const permissions = useModuloPermissions('tarjeta');
  
  const [tarjetaData, setTarjetaData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});



  useEffect(() => {
    loadTarjetaData();
  }, [id]);

  const loadTarjetaData = async () => {
    try {
      setLoading(true);
      const result = await dispatch(fetchTarjetaById(id));
      if (result.payload?.data) {
        const tarjeta = result.payload.data;
        console.log('Tarjeta cargada:', tarjeta);
        setTarjetaData(tarjeta);
        
        setEditedData({
          nombre: tarjeta.nombre || '',
          posicionesEnTrama: tarjeta.posicionesEnTrama || '',
          alwaysOn: tarjeta.alwaysOn || false,
          estatusDispositivo: tarjeta.estatusDispositivo !== undefined ? tarjeta.estatusDispositivo : true
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la tarjeta'
        });
        navigate('/tarjetas');
      }
    } catch (error) {
      console.error('Error loading tarjeta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al cargar la tarjeta: ' + error.message
      });
      navigate('/tarjetas');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAlwaysOnChange = (newValue) => {
    // Si se intenta marcar como crítico (alwaysOn = true) pero el dispositivo está apagado
    if (newValue && !editedData.estatusDispositivo) {
      Swal.fire({
        icon: 'warning',
        title: 'Dispositivo Apagado',
        text: 'Para marcar una tarjeta como crítica, primero debe estar encendida. Por favor, encienda el dispositivo antes de continuar.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ff9800'
      });
      return; // No permitir la acción
    }

    const title = newValue ? 'Activar Dispositivo Crítico' : 'Desactivar Dispositivo Crítico';
    const text = newValue
      ? 'Esta tarjeta se marcará como crítica. ¿Está seguro de que desea continuar?'
      : 'Esta tarjeta ya no será considerada crítica. ¿Está seguro de que desea continuar?';

    Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: newValue ? '#ff9800' : '#4caf50',
      cancelButtonColor: '#d33'
    }).then((result) => {
      if (result.isConfirmed) {
        handleInputChange('alwaysOn', newValue);
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!editedData.nombre?.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }
    
    if (!editedData.posicionesEnTrama || editedData.posicionesEnTrama <= 0) {
      newErrors.posicionesEnTrama = 'Las posiciones en trama deben ser mayor a 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      
      const updateData = {
        nombre: editedData.nombre.trim(),
        posicionesEnTrama: Number(editedData.posicionesEnTrama),
        alwaysOn: editedData.alwaysOn,
        estatusDispositivo: editedData.estatusDispositivo
      };
      
      await dispatch(updateTarjeta(id, updateData));
      
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Tarjeta actualizada correctamente'
      });
      
      setIsEditing(false);
      await loadTarjetaData(); // Recargar datos actualizados
      
    } catch (error) {
      console.error('Error updating tarjeta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al actualizar la tarjeta: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({
      nombre: tarjetaData.nombre || '',
      posicionesEnTrama: tarjetaData.posicionesEnTrama || '',
      alwaysOn: tarjetaData.alwaysOn || false,
      estatusDispositivo: tarjetaData.estatusDispositivo !== undefined ? tarjetaData.estatusDispositivo : true
    });
    setErrors({});
  };

  const handleCambiarEstadoTarjeta = async (tarjetaId, nuevoEstado) => {
    try {
      await dispatch(cambiarEstadoTarjeta(tarjetaId, nuevoEstado));
      await loadTarjetaData(); // Recargar datos para reflejar el cambio

      // Actualizar también editedData para que la validación funcione en tiempo real
      setEditedData(prev => ({
        ...prev,
        estatusDispositivo: nuevoEstado
      }));
    } catch (error) {
      console.error('Error al cambiar estado de la tarjeta:', error);
      // Re-lanzar el error para que el DeviceControlButton lo maneje
      throw error;
    }
  };

  const getBreadcrumbRoutes = () => {
    if (tarjetaData) {
      return routeTarjetaEdit(tarjetaData.nombre);
    }
    return [
      { path: '/tarjetas', name: 'Tarjetas' },
      { path: `/tarjetas/editar/${id}`, name: 'Editar Tarjeta' }
    ];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!tarjetaData) {
    return (
      <Box p={3}>
        <Alert severity="error">No se pudo cargar la tarjeta</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Bread routes={getBreadcrumbRoutes()} />
      
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Fab 
          size="small" 
          onClick={() => navigate('/tarjeta')}
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }
          }}
        >
          <ArrowBackIcon />
        </Fab>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          {isEditing ? 'Editando Tarjeta' : 'Detalles de la Tarjeta'}
        </Typography>
        {tarjetaData.alwaysOn && (
          <Chip 
            icon={<SecurityIcon />} 
            label="Crítico" 
            color="warning" 
            variant="outlined"
          />
        )}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        alignItems: 'flex-start',
        flexDirection: { xs: 'column', lg: 'row' }
      }}>
        {/* Información básica */}
        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MemoryIcon />
                Información de la Tarjeta
              </Typography>
              
              {permissions.puedeActualizar && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      size={isSmallScreen ? 'small' : 'medium'}
                      sx={{ minWidth: { xs: '100px', md: 'auto' } }}
                    >
                      Editar
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={saving}
                        size={isSmallScreen ? 'small' : 'medium'}
                        sx={{ minWidth: { xs: '90px', md: 'auto' } }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        size={isSmallScreen ? 'small' : 'medium'}
                        sx={{ minWidth: { xs: '90px', md: 'auto' } }}
                      >
                        {saving ? 'Guardando...' : 'Guardar'}
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={isEditing ? editedData.nombre : tarjetaData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  error={!!errors.nombre}
                  helperText={errors.nombre}
                  disabled={!isEditing}
                  InputProps={{
                    readOnly: !isEditing,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Posiciones en Trama"
                  type="number"
                  value={isEditing ? editedData.posicionesEnTrama : tarjetaData.posicionesEnTrama}
                  onChange={(e) => handleInputChange('posicionesEnTrama', e.target.value)}
                  error={!!errors.posicionesEnTrama}
                  helperText={errors.posicionesEnTrama}
                  disabled={!isEditing}
                  InputProps={{
                    readOnly: !isEditing,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Elemento"
                  value={tarjetaData.elemento?.nombre || 'Sin elemento'}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Proyecto"
                  value={tarjetaData.elemento?.proyecto?.nombre || 'Sin proyecto'}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                />
              </Grid>

              {permissions.puedeActualizar && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isEditing ? editedData.alwaysOn : tarjetaData.alwaysOn}
                        onChange={(e) => handleAlwaysOnChange(e.target.checked)}
                        disabled={!isEditing}
                        color="warning"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="warning" />
                        Dispositivo Crítico
                      </Box>
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>

        {/* Panel de control y sensores */}
        <Box sx={{ 
          width: { xs: '100%', lg: '400px' }, 
          flexShrink: 0,
          ml: { xs: 0, lg: 'auto' }
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            {/* Panel de control */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                <SensorsIcon />
                Control de Dispositivo
              </Typography>
              
              {permissions.puedeControlar ? (
                <DeviceControlButton
                  dispositivo={tarjetaData}
                  tipo="tarjeta"
                  onCambiarEstado={handleCambiarEstadoTarjeta}
                  showFullPanel={true}
                />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Estado actual:
                    </Typography>
                    <Chip 
                      label={tarjetaData.estatusDispositivo ? 'Encendido' : 'Apagado'}
                      color={tarjetaData.estatusDispositivo ? 'success' : 'error'}
                      size="small"
                      icon={tarjetaData.estatusDispositivo ? <SensorsIcon /> : <SensorsIcon />}
                    />
                  </Box>
                  
                  <Alert severity="warning" size="small">
                    <Typography variant="caption">
                      No tienes permisos para controlar este dispositivo.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>

            {/* Información de sensores */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Sensores Asociados
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Total de sensores: {tarjetaData.sensores?.length || 0}
              </Typography>
              
              {tarjetaData.sensores && tarjetaData.sensores.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {tarjetaData.sensores.map((sensor, index) => (
                    <Chip
                      key={sensor._id || index}
                      label={sensor.nombre || sensor.alias || `Sensor ${index + 1}`}
                      size="small"
                      variant="outlined"
                      color={sensor.estatusDispositivo ? 'success' : 'default'}
                      sx={{ 
                        fontSize: { xs: '0.7rem', md: '0.8125rem' },
                        height: { xs: '28px', md: '32px' }
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditTarjeta;
